import { ActionIcon, Group, Stack } from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconFocus2,
  IconMinus,
  IconPlus,
} from '@tabler/icons-react';
import { useReactFlow } from '@xyflow/react';
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePedigreeContext } from '../context/usePedigreeContext';

// -----------------------------------------------------------------------------
export const PedigreeControls: FC<{
  finishedInitialRender?: boolean;
  numberOfNodes?: number;
}> = ({ finishedInitialRender = true, numberOfNodes = 0 }) => {
  const { t } = useTranslation('pedigree');
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const { canUndo, canRedo, undo, redo, mode } = usePedigreeContext();

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (finishedInitialRender) {
      fitView({
        padding: numberOfNodes > 5 ? 0.1 : 0.5,
        includeHiddenNodes: false,
        duration: 0,
      });
    }
  }, [finishedInitialRender, fitView, numberOfNodes]);

  //----------------------------------------------------------------------------
  const handleFitView = useCallback(() => {
    fitView({
      padding: 0.1,
      includeHiddenNodes: false,
      duration: 800,
    });
  }, [fitView]);

  //----------------------------------------------------------------------------
  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 200 });
  }, [zoomIn]);

  //----------------------------------------------------------------------------
  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 200 });
  }, [zoomOut]);

  //----------------------------------------------------------------------------
  return (
    <Stack pos="absolute" top={10} right={10} style={{ zIndex: 1000 }} gap="xs">
      <Group gap="xs">
        <ActionIcon
          variant="filled"
          size="lg"
          color="blue"
          title={t('controls.zoomIn')}
          onClick={handleZoomIn}
        >
          <IconPlus size={16} />
        </ActionIcon>
        <ActionIcon
          variant="filled"
          size="lg"
          color="blue"
          title={t('controls.zoomOut')}
          onClick={handleZoomOut}
        >
          <IconMinus size={16} />
        </ActionIcon>
        <ActionIcon
          variant="filled"
          size="lg"
          color="blue"
          title={t('controls.fitView')}
          onClick={handleFitView}
        >
          <IconFocus2 size={16} />
        </ActionIcon>
        {mode === 'edit' && (
          <>
            <ActionIcon
              variant="filled"
              size="lg"
              color="blue"
              title={t('controls.undo')}
              disabled={!canUndo}
              onClick={undo}
            >
              <IconArrowBackUp size={16} />
            </ActionIcon>
            <ActionIcon
              variant="filled"
              size="lg"
              color="blue"
              title={t('controls.redo')}
              disabled={!canRedo}
              onClick={redo}
            >
              <IconArrowForwardUp size={16} />
            </ActionIcon>
          </>
        )}
      </Group>
    </Stack>
  );
};
